/* 208 §1 — WHAT IS NEEDED ONLY WHEN USED, LOADS WHEN USED.

   Laurent: "wasn't it possible instead of increasing the app main file, to
   build out of it?" A module reached only through an `onUse` loader leaves the
   main chunk — its `import()` is the only door to it — and it loads the first
   time something uses it: the Excel library when a sheet is read or written,
   the bulk mailer when it opens, Campaigns, the OneDrive picker, the call
   window.

   ONE LOADER PER MODULE. The first call starts the import; every call after it
   gets the same promise, and `now()` hands back what has already arrived, so a
   second opening draws in the same frame as the click (no empty one).

   A LOAD THAT FAILS SAYS SO IN WORDS. It rejects with `LoadFailed`, whose
   message is the sentence the reader sees, in the reader's language — so every
   door that already shows an error's message (the Excel's « ⚠ Export failed:
   … », an import dialog's own error line) says why instead of a blank, and
   `LoadedOnUse` (SharedUI.tsx) shows it with « Reload the page ».
   THE WORDS ASK FOR A RELOAD, NOT ANOTHER TRY, because the browser keeps a
   module that failed to load for the life of the page — MEASURED in both
   engines (scripts/drive-208.js --part=onuse): the chunk refused on the wire,
   then allowed, and a second import() of it fails at once without a request.
   A failed load is still forgotten here, so a browser that does retry would.

   BEFORE THESE WORDS, Phase 30's dead-tab rule (main.tsx, `vite:preloadError`):
   a tab whose bundle a deploy replaced reloads itself, at most once a minute —
   only a failure inside that minute reaches the words.

   JSDoc-typed `.mjs` (207B's method, saveQueue.mjs's reason): the house eslint
   reads a parameter named inside a TypeScript function type — `RenderOnUse`'s —
   as an unused variable, and a new file may carry no warning. */
import { tImperative } from "../i18n";

export class LoadFailed extends Error {
  /** @param {unknown} failure what the import rejected with */
  constructor(failure) {
    super(
      String(
        tImperative("common.load_failed_part", {
          fallback: "This part of the app could not be loaded — check the connection, then reload the page.",
        }),
      ),
    );
    this.name = "LoadFailed";
    /** @type {unknown} */
    this.failure = failure;
  }
}

/**
 * A loader: call it for the module (one import, however many calls); `now()`
 * is what has already arrived, `undefined` while nothing has.
 * @template M
 * @typedef {{ (): Promise<M>, now(): M | undefined }} OnUse
 */

/**
 * What `LoadedOnUse` draws with the module its loader handed back.
 * @template M
 * @typedef {(m: M) => import("react").ReactNode} RenderOnUse
 */

/**
 * @template M
 * @param {() => Promise<M>} load
 * @returns {OnUse<M>}
 */
export function onUse(load) {
  /** @type {Promise<M> | null} */
  let pending = null;
  /** @type {M | undefined} */
  let arrived;
  const get = () => {
    if (!pending)
      pending = load().then(
        (m) => (arrived = m),
        (e) => {
          pending = null;
          throw new LoadFailed(e);
        },
      );
    return pending;
  };
  return Object.assign(get, { now: () => arrived });
}

/** @typedef {typeof import("xlsx")} Xlsx */

/** The Excel library (SheetJS) — every sheet the browser reads or writes. @type {OnUse<Xlsx>} */
export const loadXlsx = onUse(() => import("xlsx"));

/* THE ONEDRIVE PICKER — the browse pane, its window and the imperative door.
   `openOneDrivePicker` keeps the picker's own signature, so its callers only
   change where they import it from; a load that fails says so on the app's
   toast and answers `null`, which is what a cancel answers — every caller
   already handles that. */

/** @typedef {typeof import("../components/OneDrivePicker")} OneDrivePickerModule */

/** @type {OnUse<OneDrivePickerModule>} */
export const loadOneDrivePicker = onUse(() => import("../components/OneDrivePicker"));

/**
 * @param {Parameters<OneDrivePickerModule["openOneDrivePicker"]>} args
 * @returns {ReturnType<OneDrivePickerModule["openOneDrivePicker"]>}
 */
export async function openOneDrivePicker(...args) {
  /** @type {OneDrivePickerModule} */
  let picker;
  try {
    picker = await loadOneDrivePicker();
  } catch (e) {
    const say = globalThis.window.__lxToast ?? globalThis.window.lxToast;
    say?.(e instanceof Error ? e.message : String(e), "error"); // 209 §25 — every toast stays its ten seconds
    return null;
  }
  return picker.openOneDrivePicker(...args);
}
